import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Grid,
  Modal,
  Paper,
  TextField,
  Autocomplete,
} from "@mui/material";
import { Cliente } from "../Shared/Interfaces/Cliente";
import SearchIcon from "@mui/icons-material/Search";
import { CepService } from "../../Services/CepService";
import { AppProvider } from "../../ContextApi/AppProvider";
import DefaultLoader from "../Shared/DefaultLoader";
import DataItem from "../Shared/Interfaces/DataItem";
import colors from "../../theme/colors";

interface ClienteModalProps {
  open: boolean;
  onClose: () => void;
  onConcluir: (clienteEditado: Cliente) => void;
  produto: Cliente;
  loading: boolean;
  canaisDeComunicacao: DataItem[];
}

const cepService = new CepService();

const ModalEditarCliente: React.FC<ClienteModalProps> = ({
  open,
  onClose,
  onConcluir,
  produto: cliente,
  loading: loading,
  canaisDeComunicacao,
}) => {
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    width: { xs: "95vw", sm: 1000 },
    height: "auto",
    transform: "translate(-50%, -50%)",
    maxWidth: "90vw",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: { xs: 2, sm: 4 },
  };

  // Create a state to manage the client being edited
  const [clienteEdicao, setClienteEdicao] = useState<Cliente>({ ...cliente });
  const contextApi = useContext(AppProvider);

  // Use useEffect to reset clienteEdicao state whenever a new cliente is passed
  useEffect(() => {
    setClienteEdicao({ ...cliente });
  }, [cliente]);

  const handleInputCliente = (fieldName: keyof Cliente, value: any) => {
    setClienteEdicao((prevCliente) => ({
      ...prevCliente,
      [fieldName]: value,
    }));
  };

  const BuscarCep = (e: React.MouseEvent) => {
    e.preventDefault();
    if (clienteEdicao.cep === "") {
      contextApi.showMessage("Informe um cep válido!", true, "error");
    } else {
      cepService
        .getCep(`https://viacep.com.br/ws/${clienteEdicao.cep}/json/`)
        .then((response) => {
          handleInputCliente("bairro", response.data.bairro);
          handleInputCliente("complemento", response.data.complemento);
          handleInputCliente("cidade", response.data.localidade);
          handleInputCliente("logradouro", response.data.logradouro);
          handleInputCliente("estado", response.data.uf);
        });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Paper sx={{ p: 1 }}>
          <Grid container spacing={1}>
            {/* Cabeçalho */}
            <Grid item xs={12}>
              <Paper sx={{ p: 0.5, bgcolor: '#f8f9fa', mb: 1 }}>
                <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                  ✏️ Editar Cliente
                </h4>
              </Paper>
            </Grid>

            {/* Seção Dados do Cliente */}
            <Grid item xs={12}>
              <Paper sx={{ p: 0.5, mb: 0.5 }}>
                <h5 style={{ margin: '0 0 10px 0', color: colors.bluePrimary }}>👤 Dados do Cliente</h5>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      required
                      name="documento"
                      label="CPF/CNPJ"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.documento}
                      onChange={(e) => handleInputCliente("documento", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      required
                      name="nome"
                      label="Nome Cliente"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.nome}
                      onChange={(e) => handleInputCliente("nome", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}></Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      required
                      name="telefone"
                      label="Telefone"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.telefone}
                      onChange={(e) => handleInputCliente("telefone", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      required
                      name="email"
                      label="E-mail"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.email}
                      onChange={(e) => handleInputCliente("email", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Autocomplete
                      fullWidth
                      options={canaisDeComunicacao}
                      getOptionLabel={(option) => option.descricao}
                      size="small"
                      value={
                        canaisDeComunicacao.find(
                          (option) => option.descricao === clienteEdicao.comoConheceu
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        handleInputCliente("comoConheceu", newValue?.descricao || "")
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Por onde conheceu a loja?" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}></Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      required
                      name="cep"
                      label="Cep"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.cep}
                      onChange={(e) => handleInputCliente("cep", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Button
                      sx={{ height: "2.5em" }}
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={BuscarCep}
                    >
                      <SearchIcon />
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      required
                      name="logradouro"
                      label="Logradouro"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.logradouro}
                      onChange={(e) =>
                        handleInputCliente("logradouro", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField
                      required
                      name="numero"
                      label="Nº"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.numero}
                      onChange={(e) => handleInputCliente("numero", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      required
                      name="complemento"
                      label="Complemento"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.complemento}
                      onChange={(e) =>
                        handleInputCliente("complemento", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      required
                      name="bairro"
                      label="Bairro"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.bairro}
                      onChange={(e) => handleInputCliente("bairro", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      required
                      name="cidade"
                      label="Cidade"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.cidade}
                      onChange={(e) => handleInputCliente("cidade", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      required
                      name="estado"
                      label="Estado"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteEdicao.estado}
                      onChange={(e) => handleInputCliente("estado", e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Botões de Ação */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button variant="outlined" onClick={onClose}>
                  Fechar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => onConcluir(clienteEdicao)}
                  disabled={loading}
                  sx={{ height: '40px' }}
                >
                  Concluir
                </Button>
                {loading && (
                  <DefaultLoader
                    height="3em"
                    width="3em"
                    colors={["#1876D2", "#1876D2", "#1876D2", "#1876D2", "#1876D2"]}
                    textInfo="Salvando cliente"
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Modal>
  );
};

export default ModalEditarCliente;
